import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
  Avatar,
  Chip,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import DiamondRoundedIcon from "@mui/icons-material/DiamondRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import GppMaybeRoundedIcon from "@mui/icons-material/GppMaybeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { LuUsersRound } from "react-icons/lu";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { IoIosTrendingUp } from "react-icons/io";
import { GoGift, GoShield } from "react-icons/go";
import { MdOutlineCampaign } from "react-icons/md";
import { LiaCoinsSolid, LiaWalletSolid } from "react-icons/lia";
import { GoClock } from "react-icons/go";
import { TbClockShield } from "react-icons/tb";
import { FaArrowUp } from "react-icons/fa";

/* ------------------------------------------------------------------ */
/* Shared design tokens — keep in sync with the rest of the app       */
/* ------------------------------------------------------------------ */
// const COLORS = {
//   purple: { bg: "#7E1BAD" },
//   green: { bg: "#D1FAE5", icon: "#10B981" },
//   blue: { bg: "#DBEAFE", icon: "#3B82F6" },
//   dark: { bg: "#111827", icon: "#FFFFFF" },
//   teal: { bg: "#CCFBF1", icon: "#14B8A6" },
//   indigo: { bg: "#E0E7FF", icon: "#6366F1" },
//   amber: { bg: "#FEF3C7", icon: "#F59E0B" },
//   gray: { bg: "#F3F4F6", icon: "#6B7280" },
//   orange: { bg: "#FFEDD5", icon: "#F97316" },
// };

/* ------------------------------------------------------------------ */
/* Reusable panel wrapper — matches PanelCard used across the app     */
/* ------------------------------------------------------------------ */
function PanelCard({ children, sx = {}, ...rest }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #EEF0F2",
        borderRadius: 3,
        p: 3,
        height: "100%",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  );
}

/* ------------------------------------------------------------------ */
/* Stat card                                                          */
/* ------------------------------------------------------------------ */
function StatCard({ icon, iconColor, label, value, suffix, delta, deltaLabel = "vs last 30 days", extra }) {
  const positive = delta === undefined ? null : delta >= 0;
  return (
        <Paper
      elevation={0}
      sx={{
        border: "1px solid #E8E8E8",
        borderRadius: 3,
        p: 3,
        height: "100%",
        p:2.5,
        boxShadow:" 0 8px 8px rgba(0, 0, 0, 0.08)"
      }}
    
    >
         <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            // variant="rounded"
            sx={{
              bgcolor:iconColor,
              color: "#ffffff",
              width: 40,
              height: 40,
              // borderRadius: "10px",
              p:1
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="body2" sx={{ color: "#000000", fontWeight: 500 }}>
            {label}
          </Typography>
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#000000" }}>
          {value}
          {suffix && (
            <Typography component="span" variant="body2" sx={{ ml: 0.75, color: "#535353" }}>
              {suffix}
            </Typography>
          )}
        </Typography>

        {delta !== undefined && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FaArrowUp size={10} color={`${positive ? "#3FBEAC" : "#A80101"}`}
            style={{transform:positive ? "none" : "scaleY(-1)"}}
          
            />
            <Typography variant="caption" sx={{ color: positive ? "#3FBEAC" : "#A80101", fontWeight: 600 }}>
              {Math.abs(delta)}%
            </Typography>
            <Typography variant="caption" sx={{ color: "#535353" }}>
              {deltaLabel}
            </Typography>
          </Stack>
        )}

        {extra}
      </Stack>
    </Paper>

  );
}

/* ------------------------------------------------------------------ */
/* Conversion funnel (lightweight, no chart-lib dependency)           */
/* ------------------------------------------------------------------ */
const FUNNEL_COLORS = ["#7E1BAD", "#3E9FF6", "#8CB369", "#FC6C4F"];

function ConversionFunnel({ steps }) {
  const viewW = 170;
  const base = viewW * 0.92; // triangle's top width
  const triH = base * (Math.sqrt(3) / 2); // equilateral triangle height
  const n = steps.length;
  const segH = triH / n;
  const cx = viewW / 2;
  const gap = 2; // thin gap between bands, like the mockup

  const widthAt = (y) => base * (1 - y / triH);

  return (
    <Box display={"flex"} gap={1} alignItems="center" >
      <Box sx={{ flexShrink: 0 }}>
        <svg width={viewW} height={triH} viewBox={`0 0 ${viewW} ${triH}`}>
          {steps.map((step, i) => {
            const yTop = i * segH;
            const yBottom = (i + 1) * segH;
            const topW = widthAt(yTop);
            const bottomW = widthAt(yBottom);
            const top = yTop + gap / 2;
            const bottom = yBottom - gap / 2;
            const isLast = i === n - 1;

            const points = isLast
              ? [
                  [cx - topW / 2, top],
                  [cx + topW / 2, top],
                  [cx, bottom],
                ]
              : [
                  [cx - topW / 2, top],
                  [cx + topW / 2, top],
                  [cx + bottomW / 2, bottom],
                  [cx - bottomW / 2, bottom],
                ];

            return (
              <polygon
                key={step.label}
                points={points.map((p) => p.join(",")).join(" ")}
                fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]}
              />
            );
          })}
        </svg>
      </Box>

      <Box sx={{  height: triH , width:"100%" }} >
        {steps.map((step, i) => (
          <Box key={step.label} width={"100%"} display={"flex"} justifyContent={"space-between"} alignItems="center" my={1} >
            <Box display={"flex"} gap={.5} alignItems={"center"} justifyContent={"flex-start"}>
      <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
                alignSelf: "center",
              }}
            />
            <Typography fontSize={12} sx={{ color: "#000000" }}>
              {step.label}
            </Typography>
            </Box>
            <Box display={"flex"} gap={.5} alignItems={"center"} justifyContent={"flex-end"}>
              
            <Typography fontSize={12} sx={{ fontWeight: 700, color: "#000000" ,}}>
              {step.value.toLocaleString()}
            </Typography>
            {step.pct !== undefined && (
              <Typography variant="caption" sx={{ color: "#535353" , mx:1}}>
                {step.pct}%
              </Typography>
            )}
            </Box>
      
          </Box>
        ))}
      </Box>
    </Box>
  );
}
/* ------------------------------------------------------------------ */
/* Recent activity feed                                               */
/* ------------------------------------------------------------------ */
function RecentActivity({ items }) {
  return (
    <Stack spacing={2}>
      {items.map((item, i) => (
        <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar src={item.avatar} sx={{ width: 32, height: 32 }}>
            {item.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: "#374151" }}>
              <Typography component="span" sx={{ fontWeight: 600, color: "#111827" }}>
                {item.name}
              </Typography>{" "}
              {item.action}
              {item.code && (
                <Typography component="span" sx={{ fontWeight: 600, color: "#111827" }}>
                  {" "}
                  {item.code}
                </Typography>
              )}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#9CA3AF", whiteSpace: "nowrap" }}>
            {item.time}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Impact banner                                                      */
/* ------------------------------------------------------------------ */
function ImpactBanner({ multiplier, revenue, stats }) {
  return (
    <PanelCard sx={{ bgcolor: "#ECFDF5", border: "1px solid #D1FAE5" }}>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: "#10B981", width: 44, height: 44 }}>
              <GroupsRoundedIcon />
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#111827" }}>
                Referral Program Impact
              </Typography>
              <Typography variant="body2" sx={{ color: "#4B5563" }}>
                Your referral program helped acquire{" "}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {multiplier}x
                </Typography>{" "}
                more new customers and generate{" "}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {revenue}
                </Typography>{" "}
                in revenue.
              </Typography>
            </Box>
          </Stack>
        </Grid>
        {stats.map((s) => (
          <Grid item xs={6} md={2} key={s.label}>
            <Typography variant="caption" sx={{ color: "#6B7280" }}>
              {s.label}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
                {s.value}
              </Typography>
              {s.delta && (
                <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>
                  ↑ {s.delta}
                </Typography>
              )}
            </Stack>
          </Grid>
        ))}
      </Grid>
    </PanelCard>
  );
}

function tierColor(tier) {
  switch (tier) {
    case "Gold":
      return { bg: "#FEF3C7", text: "#B45309" };
    case "Silver":
      return { bg: "#F3F4F6", text: "#4B5563" };
    case "Bronze":
      return { bg: "#FFEDD5", text: "#C2410C" };
    default:
      return { bg: "#F3F4F6", text: "#4B5563" };
  }
}

/* ------------------------------------------------------------------ */
/* Overview tab — content only. Header + Tabs live in Referral.jsx    */
/* ------------------------------------------------------------------ */
export default function OverviewReferral({
  stats = defaultStats,
  trendData = defaultTrendData,
  funnelSteps = defaultFunnelSteps,
  topReferrers = defaultTopReferrers,
  popularRewards = defaultPopularRewards,
  activeCampaigns = defaultActiveCampaigns,
  recentActivity = defaultRecentActivity,
  impact = defaultImpact,
}) {
  const [trendRange, setTrendRange] = useState("30");

  return (
    <Box>
      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {stats.map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>
          <Grid container spacing={2.5} alignItems="stretch">
            {/* Left */}
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p:1.5, width: '100%',}}>
         <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, }}>
                Referral Trend
              </Typography>
              <Select
                size="small"
                value={trendRange}
                onChange={(e) => setTrendRange(e.target.value)}
                sx={{ minWidth: 130, borderRadius: 2 }}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </Stack>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="invited" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3FBEAC" stopOpacity={0} />
                    <stop offset="95%" stopColor="#3FBEAC" stopOpacity={.25} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 ,}} />
                <Area type="monotone" dataKey="invited" name="Invited" stroke="#7E1BAD" fill="url(#invited)" strokeWidth={2} dot={{ r: 3 }}  />
                <Area type="monotone" dataKey="registered" name="Registered" stroke="#3E9FF6" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
                <Area type="monotone" dataKey="successful" name="Successful" stroke="#8CB369" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
                <Area type="monotone" dataKey="rewardIssued" name="Reward Issued" stroke="#FC6C4F" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
  </Card>
   
  <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p:1.5,}}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Top Referrers
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["#", "Referrer", "Tier", "Successful Referrals", "Lifetime Rewards", "Revenue Generated"].map((h) => (
                      <TableCell key={h} sx={{ color: "#9CA3AF", fontWeight: 600, border: "none" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topReferrers.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ border: "none" }}>{i + 1}</TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={r.avatar} sx={{ width: 28, height: 28 }}>
                            {r.name[0]}
                          </Avatar>
                          <Typography variant="body2">{r.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Chip
                          label={r.tier}
                          size="small"
                          sx={{
                            bgcolor: tierColor(r.tier).bg,
                            color: tierColor(r.tier).text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>{r.successfulReferrals}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.lifetimeRewards.toLocaleString()}</TableCell>
                      <TableCell sx={{ border: "none", fontWeight: 600 }}>{r.revenue.toLocaleString()} EGP</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
  </Card>
        </Box>
    
      </Grid>

{/* Right */}
      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p:1.5 }}>
     <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#000000", mb: 2 }}>
              Conversion Funnel
            </Typography>
            <ConversionFunnel steps={funnelSteps} />


        </Card>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p:1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

     <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Recent Activity
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <RecentActivity items={recentActivity} />
           </Card>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#E8E8E8", p:1.5 , my:2}}>
           
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Popular Rewards
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Reward", "Type", "Points / Value", "Redeemed"].map((h) => (
                      <TableCell key={h} sx={{ color: "#9CA3AF", fontWeight: 600, border: "none" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {popularRewards.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ border: "none", fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.type}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.value}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.redeemed.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
           
           </Card>
    
        </Box>
      </Grid>
    </Grid>
{/* 
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <PanelCard sx={{ height: 420 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, }}>
                Referral Trend
              </Typography>
              <Select
                size="small"
                value={trendRange}
                onChange={(e) => setTrendRange(e.target.value)}
                sx={{ minWidth: 130, borderRadius: 2 }}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </Stack>
            <ResponsiveContainer width="100%" height="85%">
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="invited" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3FBEAC" stopOpacity={0} />
                    <stop offset="95%" stopColor="#3FBEAC" stopOpacity={.25} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 ,}} />
                <Area type="monotone" dataKey="invited" name="Invited" stroke="#7E1BAD" fill="url(#invited)" strokeWidth={2} dot={{ r: 3 }}  />
                <Area type="monotone" dataKey="registered" name="Registered" stroke="#3E9FF6" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
                <Area type="monotone" dataKey="successful" name="Successful" stroke="#8CB369" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
                <Area type="monotone" dataKey="rewardIssued" name="Reward Issued" stroke="#FC6C4F" fill="transparent" strokeWidth={2} dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </PanelCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
          border={"1px solid #EEF0F2"}
          p={2}
          borderRadius={3}
          height={420}
          >
       <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#000000", mb: 2 }}>
              Conversion Funnel
            </Typography>
            <ConversionFunnel steps={funnelSteps} />
          </Box>
   
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <PanelCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Top Referrers
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["#", "Referrer", "Tier", "Successful Referrals", "Lifetime Rewards", "Revenue Generated"].map((h) => (
                      <TableCell key={h} sx={{ color: "#9CA3AF", fontWeight: 600, border: "none" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topReferrers.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ border: "none" }}>{i + 1}</TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar src={r.avatar} sx={{ width: 28, height: 28 }}>
                            {r.name[0]}
                          </Avatar>
                          <Typography variant="body2">{r.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Chip
                          label={r.tier}
                          size="small"
                          sx={{
                            bgcolor: tierColor(r.tier).bg,
                            color: tierColor(r.tier).text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>{r.successfulReferrals}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.lifetimeRewards.toLocaleString()}</TableCell>
                      <TableCell sx={{ border: "none", fontWeight: 600 }}>{r.revenue.toLocaleString()} EGP</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PanelCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <PanelCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Recent Activity
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <RecentActivity items={recentActivity} />
          </PanelCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <PanelCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
                Popular Rewards
              </Typography>
              <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
                View all →
              </Button>
            </Stack>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Reward", "Type", "Points / Value", "Redeemed"].map((h) => (
                      <TableCell key={h} sx={{ color: "#9CA3AF", fontWeight: 600, border: "none" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {popularRewards.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ border: "none", fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.type}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.value}</TableCell>
                      <TableCell sx={{ border: "none" }}>{r.redeemed.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PanelCard>
        </Grid>
      </Grid> */}

      {/* Active Campaigns */}
      <PanelCard sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
            Active Campaigns
          </Typography>
          <Button size="small" sx={{ textTransform: "none", color: "#10B981" }}>
            View all →
          </Button>
        </Stack>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Campaign", "Reward", "Target Audience", "Invites", "Success Rate", "Status"].map((h) => (
                  <TableCell key={h} sx={{ color: "#9CA3AF", fontWeight: 600, border: "none" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeCampaigns.map((c, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ border: "none" }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar variant="rounded" sx={{ bgcolor: c.iconColor.bg, color: c.iconColor.icon, width: 30, height: 30, borderRadius: "8px" }}>
                        {c.icon}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ border: "none" }}>{c.reward}</TableCell>
                  <TableCell sx={{ border: "none" }}>{c.audience}</TableCell>
                  <TableCell sx={{ border: "none" }}>{c.invites.toLocaleString()}</TableCell>
                  <TableCell sx={{ border: "none" }}>{c.successRate}%</TableCell>
                  <TableCell sx={{ border: "none" }}>
                    <Chip
                      size="small"
                      label={c.status}
                      icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10B981 !important", ml: "8px !important" }} />}
                      sx={{ bgcolor: "#D1FAE5", color: "#059669", fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PanelCard>

      {/* Impact banner */}
      <ImpactBanner multiplier={impact.multiplier} revenue={impact.revenue} stats={impact.stats} />
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Default mock data — replace with API data via props                */
/* ------------------------------------------------------------------ */
const defaultStats = [
  { icon: <LuUsersRound size="small" />, iconColor: "#7E1BAD", label: "Total Referrals", value: "24,568", delta: 18 },
  { icon: <IoMdCheckmarkCircleOutline  size="small" />, iconColor: "#04B22E", label: "Successful Referrals", value: "8,752", delta: 8 },
  { icon: <IoIosTrendingUp size="small" />, iconColor: "#3E9FF6", label: "Referral Conversion Rate", value: "35.6%", delta: 14 },
  { icon: <GoGift size="small" />, iconColor: "#000000", label: "Rewards Issued", value: "12,458,750" },
  { icon: <MdOutlineCampaign size="small" />, iconColor:"#3FBEAC", label: "Active Campaigns", value: "12", extra: (
      <Typography variant="caption" sx={{ color: "#000000", cursor: "pointer" }}>View all Campaigns ›</Typography>
    ) },
  { icon: <LiaCoinsSolid size="small" />, iconColor: "#6155F5", label: "Avg Reward per Referral", value: "150", suffix: "Points", delta: 14 },
  { icon: <GoClock size="small" />, iconColor:"#FEB91E", label: "Pending Rewards", value: "1,245,800" },
  { icon: <TbClockShield size="small" />, iconColor: "#696969", label: "Referral Expired", value: "2,145", delta: 9 },
  { icon: <GoShield size="small" />, iconColor: "#FC6C4F", label: "Fraudulent Referrals", value: "326", delta: 9 },
  { icon: <LiaWalletSolid size="small" />, iconColor: "#8CB369", label: "Revenue Generated", value: "4,875,600", suffix: "EGP", delta: 14 },
];

const defaultTrendData = [
  { date: "Apr 28", invited: 75000, registered: 45000, successful: 15000, rewardIssued: 5000 },
  { date: "May 05", invited: 110000, registered: 65000, successful: 30000, rewardIssued: 10000 },
  { date: "May 12", invited: 220000, registered: 95000, successful: 35000, rewardIssued: 12000 },
  { date: "May 19", invited: 165000, registered: 90000, successful: 55000, rewardIssued: 40000 },
  { date: "May 26", invited: 225000, registered: 130000, successful: 65000, rewardIssued: 42000 },
  { date: "May 31", invited: 250000, registered: 160000, successful: 95000, rewardIssued: 90000 },
];

const defaultFunnelSteps = [
  { label: "Invites", value: 24568 },
  { label: "Registered", value: 15860, pct: 64.6 },
  { label: "Completed First Order", value: 8725, pct: 35.5 },
  { label: "Rewards Issued", value: 8102, pct: 32.9 },
];

const defaultTopReferrers = Array.from({ length: 5 }).map((_, i) => ({
  name: "Ahmed Mohamed",
  tier: ["Gold", "Gold", "Gold", "Silver", "Bronze"][i],
  successfulReferrals: 48,
  lifetimeRewards: 12800,
  revenue: 25600,
}));

const defaultPopularRewards = [
  { name: "200 Points", type: "Points", value: "200 pts", redeemed: 4256 },
  { name: "100 EGP Cashback", type: "Cashback", value: "100 EGP", redeemed: 3215 },
  { name: "50 EGP Voucher", type: "Voucher", value: "50 EGP", redeemed: 2284 },
  { name: "Free Car Wash", type: "Service", value: "-", redeemed: 1985 },
  { name: "Free Inspection", type: "Service", value: "-", redeemed: 1235 },
];

const defaultActiveCampaigns = [
  { name: "Invite Friends", icon: <CardGiftcardRoundedIcon fontSize="small" />, iconColor: "", reward: "200 Points", audience: "All Customers", invites: 12568, successRate: 38.2, status: "Active" },
  { name: "Summer Referral", icon: <CardGiftcardRoundedIcon fontSize="small" />, iconColor: "", reward: "100 EGP Cashback", audience: "All Customers", invites: 8245, successRate: 34.6, status: "Active" },
  { name: "Back to School", icon: <CardGiftcardRoundedIcon fontSize="small" />, iconColor: "", reward: "150 Points", audience: "New Customers", invites: 3455, successRate: 31.1, status: "Active" },
  { name: "VIP Referral", icon: <CardGiftcardRoundedIcon fontSize="small" />, iconColor: "", reward: "500 Points", audience: "VIP Customers", invites: 1234, successRate: 45.8, status: "Active" },
];

const defaultRecentActivity = [
  { name: "Ahmed Ali", action: "invited", code: "Sara Ahmed", time: "20m ago" },
  { name: "Sara Ahmed", action: "registered using code", code: "AHMED25", time: "1h ago" },
  { name: "Mona Salah", action: "completed first order", time: "1h ago" },
  { name: "Omar Ali", action: "registered using code", code: "MONA10", time: "2d ago" },
];

const defaultImpact = {
  multiplier: "2.4",
  revenue: "4.8M EGP",
  stats: [
    { label: "New Customers", value: "2,540", delta: "18%" },
    { label: "Referral Conversion Rate", value: "38.5%", delta: "9.3%" },
    { label: "Revenue Generated", value: "4.8M EGP", delta: "8.7%" },
    { label: "Referral ROI", value: "5.2x", delta: "15.2%" },
  ],
};